import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { push } from "react-router-redirect";
import {
  displayActionableToast,
  displayToast,
} from "../../components/app/AppToast";
import { createMemoryStore } from "../../lib/store";
import { apiClient, useApiQuery, type Api } from "../../services/api";
import type { ChatSuggestion } from "../../domains/gpt/hooks/useChatSuggestion";
import {
  gaEventVisualAbstractCtaShown,
  gaEventVisualAbstractRequested,
} from "../../tools/analytics/domains/article.analytics";
import { watchVisualAbstract } from "./visualAbstractWatchStore";

/**
 * The papers whose offer the reader has waved away, for this tab only.
 *
 * Deliberately NOT persisted. A dismissal here means "not while I am reading
 * this", not "never again": the offer is true for as long as the paper is, and
 * a durable no would silently retire the feature for a reader who tapped the
 * cross once to get their composer back. The Room proposal's own dismissals are
 * in-memory for the same reason.
 */
const useDismissedVisualAbstracts = createMemoryStore<string[]>([]);

/**
 * Everything the composer band shows about this paper's poster: the offer to
 * make one, and whether one is being made right now.
 *
 * Both come from the same list query, so they are answered together rather than
 * by two hooks racing on one cache entry. They are separate fields because they
 * occupy different slots: an offer TAKES the band over, a run in progress
 * stacks above the composer and leaves it usable.
 *
 * `suggestion` is null for reasons that are not interchangeable: the flag is
 * off, the verdict has not landed, the paper is REFUSED, a poster already
 * exists, or the reader dismissed it.
 */
export function useVisualAbstractBand({
  articleId,
  articleTitle,
  roomId,
}: {
  articleId: string;
  /** Rides into the watch entry, so the completion toast names this paper. */
  articleTitle: string;
  roomId: string | null;
}): {
  // The arm itself, not the whole union: this hook only ever produces one, and
  // returning `ChatSuggestion` would force every caller that reads a field to
  // re-narrow a union it knows cannot be anything else. It stays assignable
  // where the band wants a `ChatSuggestion`.
  suggestion: Extract<ChatSuggestion, { kind: "visual_abstract" }> | null;
  isGenerating: boolean;
} {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useDismissedVisualAbstracts();

  const refetchInterval = useCallback(
    (
      data:
        | Api["get"]["/webapp/private/articles/:id/visual-abstracts"]["output"]
        | undefined
    ) =>
      data !== undefined &&
      data.body.artifacts.some(
        (artifact) =>
          artifact.status === "queued" || artifact.status === "generating"
      )
        ? 3_000
        : false,
    []
  );

  // The key the transcript card and the header sheet already use, so a run
  // started from the band is reflected in both without a second request.
  const listQuery = useApiQuery(
    "/webapp/private/articles/:id/visual-abstracts",
    { param: { id: articleId }, query: { roomId }, refetchInterval }
  );

  const body = listQuery.state === "success" ? listQuery.body : null;
  const latest = body === null ? undefined : body.artifacts.at(0);
  const isGenerating =
    latest !== undefined &&
    (latest.status === "queued" || latest.status === "generating");

  const nothingToOffer =
    body === null ||
    // The refusal is NOT reported here. `VisualAbstractCard` renders on this
    // same screen and already reports it from an effect; emitting from a hook
    // body would be a side effect in render, fired again on every re-render and
    // twice under StrictMode.
    body.eligibility.state === "ineligible" ||
    // A paper that already has a poster — running, ready or failed — is not one
    // to offer again.
    body.artifacts.length > 0 ||
    dismissed.includes(articleId);

  async function generate(): Promise<void> {
    try {
      const response = await apiClient.post(
        "/webapp/private/articles/:id/visual-abstracts",
        { param: { id: articleId }, json: { roomId } }
      );
      // Registered from the 202: generation outlives this screen, and a run
      // nobody is watching lands with no toast.
      watchVisualAbstract({ id: response.body.id, articleId, articleTitle });
      gaEventVisualAbstractRequested({
        articleId,
        roomId,
        sourceType: response.body.sourceType,
        trigger: "band",
      });
      await listQuery.refetch();
      displayActionableToast(
        {
          title: t("Visual abstract generation started"),
          description: t("Follow its progress in Visual abstracts"),
        },
        "success",
        () => push("/visual-abstracts")
      );
    } catch {
      // The cause is already captured by the api client's onRequestError; the
      // reader gets one stable sentence rather than a server string.
      displayToast(t("Unable to generate the visual abstract"), "error");
    }
  }

  return {
    isGenerating,
    suggestion: nothingToOffer
      ? null
      : {
          kind: "visual_abstract",
          articleId,
          onShown: () => {
            gaEventVisualAbstractCtaShown({
              articleId,
              roomId,
              affordance: "generate",
              trigger: "band",
            });
          },
          onGenerate: () => {
            void generate();
          },
          onDismiss: () => {
            setDismissed((previous) => [...previous, articleId]);
          },
        },
  };
}
